import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { FaCircleCheck } from 'react-icons/fa6'

import { loginWithPassword, requestMagicLink } from '@/services/contribution/api.tsx'
import useSession from '@/hooks/useSession'
import { sendAuthErrorEvent, sendRequestMagicLinkEvent, sendSigninPasswordEvent } from '@/services/analytics/gtm'

import { getRegistrationURL } from '@/util/helpers'
import Section from '@components/Section'
import Button from '@components/Button'
import SchoolLogo from '@/components/SchoolLogo'
import PasswordSignin from './PasswordSignin'
import MagicLinkSignin from './MagicLinkSignin'

interface SigninFormProps {
  schoolSlug: string
  schoolName: string
  schoolLogo?: string
}

export default function SigninForm({ schoolSlug, schoolName, schoolLogo }: SigninFormProps) {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const navigate = useNavigate()

  const { startSession } = useSession()

  async function onPasswordSignin(email: string, password: string): Promise<boolean> {
    try {
      if (!schoolSlug) {
        Sentry.logger.warn(`School slug prop is undefined on sign in page. Trying again.`)
        return false
      }

      if (!email || !password) {
        throw new Error('Email and password are both required.')
      }

      const { accessToken, eventId, participationId, parentName, parentSlug, schoolName, isParticipationMutable } =
        await loginWithPassword(schoolSlug, email, password)

      startSession({
        accessToken,
        eventId: eventId.toString(),
        participationId: participationId.toString(),
        parentName,
        parentSlug,
        schoolName: schoolName ?? '',
        schoolSlug,
        isParticipationMutable,
      })

      Sentry.logger.info(Sentry.logger.fmt`Authenticated via Password Login: (${participationId})`)
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Authenticated via Password Login',
        level: 'info',
        data: {
          participationId,
          parentName,
          parentSlug,
          schoolName,
          schoolSlug,
        },
      })

      sendSigninPasswordEvent()
      navigate(`/${schoolSlug}/onboarding`)
      return true
    } catch (error) {
      let toastMessage = t('content.components.signin.password.failure_toast')
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: {
            location: 'Password Signin',
          },
          extra: {
            schoolSlug,
            email,
            errorMessage: error?.message,
            stack: error.stack,
          },
        })

        if (error.message === 'Registration Closed.') {
          toastMessage = t('content.components.signin.registration_closed_toast')
        }

        sendAuthErrorEvent({ action: 'signin_password', errorCause: error.message })
      }

      toast.error(toastMessage, {
        autoClose: false,
      })
      return false
    }
  }

  async function onMagicLinkSignin(email: string): Promise<boolean> {
    if (!email) return false

    const success = await requestMagicLink(schoolSlug, email)
    if (success) {
      sendRequestMagicLinkEvent()
      toast.success(t('content.components.signin.magiclink.success_toast'))
    } else {
      sendAuthErrorEvent({ action: 'request_magic_link' })
      toast.error(t('content.components.signin.magiclink.error_toast'))
    }

    setShowConfirmation(success)
    return success
  }

  if (showConfirmation) {
    return (
      <Section wrapperStyle='my-8 justify-center items-center gap-4'>
        <SchoolLogo url={schoolLogo} />
        <div className='w-full text-center font-serif text-xl font-bold mt-4 mb-8'>{schoolName}</div>

        <div className='flex flex-col gap-4 justify-center items-center h-full text-center'>
          <div className='flex flex-row items-center gap-2'>
            <FaCircleCheck className='text-leaf-green h-6 w-6' />
            <h2 className='text-2xl font-serif font-semibold text-leaf-green'>
              {t('content.components.signin.magiclink.success_title')}
            </h2>
          </div>
          <p className='text-gray-700 '>{t('content.components.signin.magiclink.success_subtitle')}</p>
        </div>
        <hr className='my-4 text-neutral-300' />
        <div className='text-sm text-center mb-4'>{t('content.components.signin.no_account')}</div>
        <div className='flex flex-row justify-center'>
          <Link
            to={getRegistrationURL(schoolSlug)}
            className='w-auto text-cta text-center hover:text-cta-active hover:underline'
          >
            {t('content.components.registration.title')}
          </Link>
        </div>
      </Section>
    )
  }

  return (
    <Section wrapperStyle='my-8 justify-center items-center gap-4'>
      <SchoolLogo url={schoolLogo} />
      <div className='w-full text-center font-serif text-xl font-bold mt-4 mb-8'>{schoolName}</div>

      <div className='flex flex-row w-full justify-between gap-x-4 mb-8'>
        <Button
          fullwidth
          variant={showPassword ? 'alt' : 'highlight'}
          label={t('content.signin_page.magiclink_option')}
          onClick={() => setShowPassword(false)}
        />

        <Button
          fullwidth
          variant={showPassword ? 'highlight' : 'alt'}
          label={t('content.signin_page.password_option')}
          onClick={() => setShowPassword(true)}
        />
      </div>
      {showPassword ? (
        <PasswordSignin onPasswordSignin={onPasswordSignin} />
      ) : (
        <MagicLinkSignin onMagicLinkSignin={onMagicLinkSignin} />
      )}

      <hr className='my-4 text-neutral-300' />
      <div className='text-sm text-center mb-4'>{t('content.components.signin.no_account')}</div>
      <div className='flex flex-row justify-center'>
        <Link
          to={getRegistrationURL(schoolSlug)}
          className=' text-cta text-center hover:text-cta-active hover:underline'
        >
          {t('content.components.registration.title')}
        </Link>
      </div>
    </Section>
  )
}
